import { useCallback } from 'react'
import {
  useSetSpeakerMutation,
  BroadcastVideoLayout,
  useSignalAttendeeMutation,
  SignalCommand
} from 'graphql/generated'
import { useEventVideoContext } from 'context/EventVideoContext'
import { useCurrentUser, useQueryErrorHandler } from 'hooks'
import useEndSpeaker from './useEndSpeaker'
import { useOptionsModal } from 'components/modals/OptionsModal'

interface IProps {
  id: string
  name: string
  onCompleted?: Callback
}

function useVideoTileOptions({ id, name, onCompleted }: IProps) {
  const { event, currentSpeakers } = useEventVideoContext()
  const { id: currentUserId } = useCurrentUser()

  const isSelfTile = id === currentUserId
  const isHost = event.isOwner
  const isMultiTile = (currentSpeakers?.length || 0) >= 2

  const onError = useQueryErrorHandler(
    'Failed to complete operation. Please try again later.'
  )
  const [disableVideo] = useSignalAttendeeMutation({
    variables: {
      signal: {
        eventId: event.id,
        userId: id,
        command: SignalCommand.STOP_VIDEO,
        data: ''
      }
    },
    onError,
    onCompleted
  })
  const [endSpeaker] = useEndSpeaker(id, onCompleted)
  const [endAllSpeakers] = useSetSpeakerMutation({
    variables: {
      eventId: event.id,
      layout: BroadcastVideoLayout.HOST
    },
    onError,
    onCompleted
  })

  const showOptions = useOptionsModal()
  const doShowMenu = useCallback(
    () =>
      showOptions({
        title: 'Speaker Controls',
        body: 'Select the action you wish to take',
        options: [
          isSelfTile &&
            !isHost && {
              icon: 'stop-circle',
              label: `Finish Speaking`,
              onClick: endSpeaker
            },
          !isSelfTile &&
            isHost && {
              icon: 'video-slash',
              label: `Make Speaker Audio Only`,
              info: `Disable ${name}'s video`,
              onClick: disableVideo
            },
          !isSelfTile &&
            isHost && {
              icon: 'stop-circle',
              label: `End Speaker`,
              info: `Remove ${name} as a speaker`,
              onClick: endSpeaker
            },
          isHost &&
            isMultiTile && {
              icon: 'stop-circle',
              label: `End All Presenters`,
              info: `Remove all speakers but yourself`,
              onClick: endAllSpeakers
            }
        ]
      }),
    [
      isSelfTile,
      isHost,
      isMultiTile,
      endSpeaker,
      endAllSpeakers,
      disableVideo,
      name,
      showOptions
    ]
  )

  // proxy trigger; there's no need to show the menu to
  // anyone but a preseter tapping on their own tile options,
  // or a host tapping on any tile's options
  const showMenu = useCallback(() => {
    if (!isHost && !isSelfTile) {
      return
    }

    doShowMenu()
  }, [isHost, isSelfTile, doShowMenu])

  return showMenu
}

export default useVideoTileOptions
